import { apiClient } from "@/api/apiClient";
import { type DistanceUnit, distanceUnits } from "@/models/siteUnit.types";

/** v2-server `geometryToDxfSchema.objectType` と同じ集合。レイヤ名の決定に使う */
export const dxfObjectTypes = [
	"road",
	"flat_ground",
	"flatground",
	"trench",
	"foundation_excavation",
	"spreading",
] as const;
export type DxfObjectType = (typeof dxfObjectTypes)[number];

/** 未知の object type は legacy と同じく `road` レイヤに落とす */
export function toDxfObjectType(value: unknown): DxfObjectType {
	return dxfObjectTypes.find((t) => t === value) ?? "road";
}

/** 現場設定の距離単位を DXF API の単位に正規化する。未知・未設定は m */
export function toDxfDistanceUnit(value: unknown): DistanceUnit {
	return (
		Object.values(distanceUnits).find((u) => u === value) ??
		distanceUnits.METERS
	);
}

export interface IDxfExportRequest {
	siteId: string;
	name: string;
	objectType: DxfObjectType;
	/** 既に `distanceUnit` に換算済みの頂点。サーバは値を変換しない */
	vertices: number[];
	indices: number[];
	distanceUnit: DistanceUnit;
}

/**
 * v2-server `POST /:siteId/object/dxf` でメッシュを DXF (R2007, 3DFACE) に変換して受け取る。
 * legacy `POST /objects/dxf` の後継。レスポンスは gzip だがブラウザが透過的に展開するので
 * Blob は平文 DXF。
 * @returns DXF の Blob
 */
export async function fetchObjectDxfBlob(
	request: Readonly<IDxfExportRequest>,
): Promise<Blob> {
	const { siteId, ...body } = request;
	if (!siteId) throw new Error("fetchObjectDxfBlob: siteId is required");

	const res = await apiClient.v2.api[":siteId"].object.dxf.$post({
		param: { siteId },
		json: body,
	});
	if (!res.ok) {
		throw new Error(`API error: ${res.status} ${res.statusText}`);
	}
	return res.blob();
}
